import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { toast } from 'sonner';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { getIndustryName } from '../../../../models/UiUtils';
import {
	getHwcIndustryCategoryTemplatesCache,
	invalidateHwcIndustryCategories,
	prefetchHwcIndustryCategoryTemplates,
	setHwcIndustryCategoriesCache,
	setHwcIndustryCategoryTemplatesCache,
	useAdminTemplateSearchQuery,
	useDeleteHwcCategoryMutation,
	useHwcCategoryOrderMutation,
	useHwcCategoryTemplatesOrderMutation,
	useHwcIndustryCategories,
	useIndustryCategoryMutation,
} from '../../../../queries';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
} from '../../../../web/components-v2/Dropdown';
import { DeleteConfirmation } from '../../../../web/components/DeleteConfirmation';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { SearchIcon } from '../../../../web/components/svgs/icons/SearchIcon';
import { TrashIcon } from '../../../../web/components/svgs/icons/TrashIcon';
import { TextInput } from '../../../../web/components/TextInput';
import { header } from '../../../../web/styles/colors';
import { baseStyleSheet, bs } from '../../../../web/styles/styles';
import {
	CampaignTemplatesByIndustrySearchTable,
	CampaignTemplatesByIndustrySearchTableCell,
	CampaignTemplatesByIndustrySearchTableRow,
} from '../CampaignTemplatesByIndustry/components/CampaignTemplatesByIndustryHwcSearchResults';
import { AccordionContentCategoryTemplates } from '../TemplateCategory/AccordionContentCategoryTemplates';
import { useHwcTemplateActions } from '../TemplateCategory/hooks/useHwcTemplateActions';
import { AccordionDnDRoot, AccordionDraggableContent, AccordionDraggableTrigger } from './AccordionDnD';
import { NewCategoryModal } from './presentation';
import { styleSheet } from './styles';

export function TabContentIndustryCategorizedCards({ industry }: { industry: Api.Industry }) {
	const [newCategoryModalOpen, setNewCategoryModalOpen] = React.useState<boolean>(false);

	const [categoryToDelete, setCategoryToDelete] = React.useState<string>(null);
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const fullscreenModal = useFullscreenModal();
	const { logApiError, logEvent } = useEventLogging('TabContentIndustryCategorizedTemplates');
	const [searchInputValue, setSearchInputValue] = React.useState('');
	const categoriesQuery = useHwcIndustryCategories({ industry });
	const debouncedSearchValue = useDebounceValue(searchInputValue);
	const isAdminTemplateSearchQueryEnabled = Boolean(debouncedSearchValue);
	const adminTemplateSearchQuery = useAdminTemplateSearchQuery({
		enabled: isAdminTemplateSearchQueryEnabled,
		request: {
			industry,
			section: Api.TemplateSection.HandwrittenCard,
			searchValue: debouncedSearchValue,
		},
	});
	const { handleArchiveTemplate, handleFeatureTemplate, handleViewTemplate, handleEditTemplate } =
		useHwcTemplateActions({ industry });
	const categoryOrderMutation = useHwcCategoryOrderMutation({
		onError: error => {
			toast.error('Unable to rearrange categories');
			logApiError('Reorder-Error', error);
		},
		onSuccess: (_, variables) => {
			invalidateHwcIndustryCategories({ industry });
			logEvent('ReorderCategory', {
				industry: variables.industry,
				newCategoryOrder: variables.names,
			});
		},
	});
	const categoryTemplatesOrderMutation = useHwcCategoryTemplatesOrderMutation({
		onError: error => {
			toast.error("Unable to rearrange category's templates");
			logApiError('Reorder-Error', error);
		},
		onSuccess: (_, variables) => {
			logEvent('ReorderCategory', {
				category: variables.categoryName,
				industry: variables.industry,

				newCategoryOrder: variables.ids,
			});
		},
	});
	const createCategoryMutation = useIndustryCategoryMutation({
		onError: error => {
			errorMessages.push({
				messages: ['Failed to create category'],
			});
			logApiError('CreateHWCCategory', error);
		},
		onSuccess: () => {
			invalidateHwcIndustryCategories({ industry });
		},
	});
	const deleteCategoryMutation = useDeleteHwcCategoryMutation({
		onError: error => {
			errorMessages.push({
				messages: ['Failed to delete category'],
			});
			logApiError('DeleteCategory-Error', error);
		},
		onSuccess: () => {
			invalidateHwcIndustryCategories({ industry });
		},
	});
	const onDragEnd = (result: DropResult) => {
		if (result.type === 'category') {
			if (!result.destination) {
				return;
			}

			const categories = [...categoriesQuery.data];
			const fromIndex = categories.findIndex(c => c.name === result.draggableId);
			const category = categories[fromIndex];
			const toIndex = result.destination.index;

			categories.splice(fromIndex, 1);
			categories.splice(toIndex, 0, category);
			categoryOrderMutation.mutate({
				industry,
				names: categories.map(c => c.name),
			});
			// Update the local cache optimistically to avoid a flash of the old order
			setHwcIndustryCategoriesCache({
				data: categories,
				industry,
			});
			return;
		}
		if (result.type.startsWith('templates-')) {
			if (!result.destination) {
				return;
			}
			const categoryName = result.destination.droppableId;
			// draggableId for templates is "<categoryName>:<templateId>"
			const templateId = result.draggableId.split(':')[1];
			// We need to read the templates from the cache because the query isn't defined at this level but within children expandables.
			// But this isn't a problem since we can rely on the data being present and current.
			// The awkwardness is a consequence of the inflexibility of react-beautiful-dnd forcing us to handle all nested draggables at the top-level here.
			const templates = getHwcIndustryCategoryTemplatesCache({ categoryName, industry });
			if (!templates) {
				toast.error('Unable to rearrange templates');
				return;
			}
			const newTemplates = [...templates];

			const fromIndex = newTemplates.findIndex(t => t.template.id === templateId);
			const template = newTemplates[fromIndex];
			const toIndex = result.destination.index;
			newTemplates.splice(fromIndex, 1);
			newTemplates.splice(toIndex, 0, template);
			categoryTemplatesOrderMutation.mutate({
				categoryName,

				ids: newTemplates.map(t => t.template.id),
				industry,
			});
			// Update the local cache optimistically to avoid a flash of the old order
			setHwcIndustryCategoryTemplatesCache({
				categoryName,
				data: newTemplates,
				industry,
			});
		}
	};
	const onDeleteCategory = (categoryName: string) => (ev: React.MouseEvent) => {
		ev.stopPropagation();
		setCategoryToDelete(categoryName);
	};
	return (
		<div>
			<div className={css(bs.flex, bs.itemsCenter, bs.boxBorder, bs.justifyBetween, bs.pt2, bs.pb8)}>
				<div className={css(styleSheet.headerTitle)}>{getIndustryName(industry)}</div>
				<div className={css(bs.flex, bs.itemsCenter, bs.gap4)}>
					<TextInput
						inputId='admin-template-search-input'
						type='text'
						value={searchInputValue}
						onChange={ev => setSearchInputValue(ev.target.value)}
						leftAccessory={<SearchIcon className={css(bs.mr2)} />}
						className={css(bs.roundedLg)}
						placeholder='Search cards'
					/>
					<button
						className={css(baseStyleSheet.ctaButton)}
						onClick={() => fullscreenModal.history.push('/campaign-templates/hwc/create')}
					>
						<span>New Card</span>
					</button>
					<button className={css(baseStyleSheet.ctaButton)} onClick={() => setNewCategoryModalOpen(true)}>
						<span>Add New Category</span>
					</button>
				</div>
			</div>
			<div className={css(styleSheet.body)}>
				{isAdminTemplateSearchQueryEnabled && adminTemplateSearchQuery.isPending ? (
					<div className={css(bs.flex, bs.wFull, bs.justifyCenter)}>
						<LoadingSpinner />
					</div>
				) : null}
				{adminTemplateSearchQuery.status === 'success' ? (
					<CampaignTemplatesByIndustrySearchTable
						firstColumnHeader={
							<>
								<strong>{adminTemplateSearchQuery.data.length}</strong> results for &quot;{debouncedSearchValue}&quot;
							</>
						}
					>
						{adminTemplateSearchQuery.data.map(template => {
							return (
								<CampaignTemplatesByIndustrySearchTableRow key={`search-results-${template.template.id}`}>
									<CampaignTemplatesByIndustrySearchTableCell>
										<span className={css(bs.textBrandPrimary, bs.textBase)}>{template.template.name}</span>
									</CampaignTemplatesByIndustrySearchTableCell>
									<CampaignTemplatesByIndustrySearchTableCell>
										<span className={css(bs.textSm)}>
											{Object.keys(template.industries)
												.map(i => i)
												.join(', ')}
										</span>
									</CampaignTemplatesByIndustrySearchTableCell>
									<CampaignTemplatesByIndustrySearchTableCell>
										<span className={css(bs.textSm)}>
											{template.template.creationDate
												? moment(template.template.creationDate).format('MM/DD/YYYY')
												: ''}
										</span>
									</CampaignTemplatesByIndustrySearchTableCell>
									<CampaignTemplatesByIndustrySearchTableCell>
										<Dropdown>
											<DropdownEllipsisTrigger />
											<DropdownContent>
												<DropdownItem onClick={() => handleViewTemplate({ template })}>View</DropdownItem>
												<DropdownItem onClick={() => handleEditTemplate({ template })}>Edit</DropdownItem>
												<DropdownItem onClick={() => handleFeatureTemplate({ template })}>Feature</DropdownItem>
												<DropdownItem onClick={() => handleArchiveTemplate({ template })}>Archive</DropdownItem>
											</DropdownContent>
										</Dropdown>
									</CampaignTemplatesByIndustrySearchTableCell>
								</CampaignTemplatesByIndustrySearchTableRow>
							);
						})}
					</CampaignTemplatesByIndustrySearchTable>
				) : null}
				{categoriesQuery.data ? (
					<AccordionDnDRoot onDragEnd={onDragEnd} topLevelDroppableId={industry} topLevelDroppableType='category'>
						{categoriesQuery.data.map((category, index) => {
							const unpublishedCount = category.templates.filter(t => t.status === Api.TemplateStatus.Draft).length;
							const canRemoveCategory =
								category.name !== 'Featured' && category.name !== 'Archived' && category.name !== 'Uncategorized';
							return (
								<AccordionDraggableTrigger
									key={category.name}
									index={index}
									draggableId={category.name}
									onMouseEnterTrigger={() => {
										prefetchHwcIndustryCategoryTemplates({
											categoryName: category.name,
											industry,
											staleTime: 10000,

											userSession,
										});
									}}
									header={
										<div className={css(styleSheet.draggableAccordionHeader)}>
											<p
												className={css(
													styleSheet.draggableAccordionHeaderTitle,
													category.name === 'Uncategorized' &&
														category.total > 0 &&
														styleSheet.draggableAccordionHeaderTitleUncategorized
												)}
											>
												{category.name}
											</p>
											<div>
												{unpublishedCount > 0 && (
													<span className={css(styleSheet.draggableAccordionHeaderUnpublished)}>
														{`${unpublishedCount} unpublished template${unpublishedCount > 1 ? 's' : ''}`}
													</span>
												)}
												<span
													className={css(
														styleSheet.draggableAccordionHeaderTotal,
														!canRemoveCategory && styleSheet.draggableAccordionHeaderTotalExtendedMargin
													)}
												>
													{category.total}
												</span>
												{canRemoveCategory ? (
													<span onClick={onDeleteCategory(category.name)}>
														<TrashIcon
															className={css(styleSheet.draggableAccordionHeaderTrashIcon)}
															fillColor={header}
														/>
													</span>
												) : null}
											</div>
										</div>
									}
								>
									<AccordionDraggableContent>
										<AccordionContentCategoryTemplates category={category} industry={industry} />
									</AccordionDraggableContent>
								</AccordionDraggableTrigger>
							);
						})}
					</AccordionDnDRoot>
				) : (
					<LoadingSpinner />
				)}
			</div>
			<NewCategoryModal
				isOpen={newCategoryModalOpen}
				onCancel={() => setNewCategoryModalOpen(false)}
				onSave={(categoryName: string) => {
					createCategoryMutation.mutate({ industry, name: categoryName });
					setNewCategoryModalOpen(false);
				}}
			/>
			<DeleteConfirmation
				bodyText={`Are you sure you want to delete ${categoryToDelete}?  All templates in ${categoryToDelete} will be moved to the Uncategorized section.`}
				destructiveButtonText='Delete Category'
				isOpen={!!categoryToDelete}
				onFinish={shouldDelete => {
					if (!shouldDelete) {
						setCategoryToDelete(null);
						return;
					}
					deleteCategoryMutation.mutate({ categoryName: categoryToDelete, industry });

					setCategoryToDelete(null);
				}}
				showSpinner={deleteCategoryMutation.isPending}
			/>
		</div>
	);
}
